import { GlassPanel } from '@/components/glass/GlassPanel';
import { cn } from '@/lib/utils';

export interface PropertyDetailSkeletonProps {
  className?: string;
}

/**
 * `<PropertyDetailSkeleton>` — composite loading skeleton for the
 * `/app/properties/:id` route. Per UI-SPEC § 4.4. Composes 4 sub-skeletons:
 *
 * 1. Header — glass-strong rectangle (address + meta + neighbors row + 96
 *    badge square).
 * 2. Gallery — glass-base outer with aspect-video main + 4 thumbnail squares.
 * 3. Radar — glass-base 280×280 panel with pentagon outline placeholder.
 * 4. Tabs — glass-strong panel with skeleton tab strip + 6 paragraph blocks.
 *
 * Animation: `animate-pulse` per Phase 05 LoadingSkeleton convention. The
 * reduced-motion `@media` rule in the global stylesheet zeroes animation
 * duration for users who request reduced motion.
 *
 * Layout matches the detail-page layout (UI-SPEC § 4.1) so the perceived
 * layout doesn't shift when real data arrives.
 */
export function PropertyDetailSkeleton({ className }: PropertyDetailSkeletonProps) {
  return (
    <div
      aria-hidden
      aria-busy="true"
      aria-label="Loading property details"
      className={cn('flex flex-col gap-6', className)}
    >
      {/* Header skeleton */}
      <GlassPanel
        variant="strong"
        className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 md:gap-6 p-4 md:p-6 items-start md:items-center animate-pulse"
      >
        <div className="flex flex-col gap-2">
          <div className="h-7 bg-ocean-800/60 rounded w-2/3" />
          <div className="h-5 bg-ocean-800/60 rounded w-1/3" />
          <div className="h-7 bg-ocean-800/60 rounded-full w-1/2 mt-1" />
        </div>
        <div className="w-24 h-24 bg-ocean-800/60 rounded-2xl md:justify-self-end" />
      </GlassPanel>

      {/* Gallery + Radar two-column row (md+); single column mobile */}
      <div className="grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-6">
        {/* Gallery skeleton */}
        <GlassPanel variant="base" className="overflow-hidden p-0 animate-pulse">
          <div className="aspect-video bg-ocean-800/80" />
          <div className="hidden md:flex gap-2 p-2 h-20 bg-ocean-900/40">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="w-24 h-full bg-ocean-800/60 rounded-lg shrink-0"
              />
            ))}
          </div>
        </GlassPanel>

        {/* Radar + InfoBadges skeleton */}
        <div className="flex flex-col gap-4">
          <GlassPanel
            variant="base"
            className="p-6 grid place-items-center animate-pulse min-h-[240px]"
          >
            <div className="w-[200px] h-[200px] md:w-[240px] md:h-[240px] lg:w-[280px] lg:h-[280px] rounded-full border-2 border-ocean-800/60" />
          </GlassPanel>
          <div className="flex flex-wrap gap-2">
            <div className="h-10 w-44 bg-ocean-800/60 rounded-full animate-pulse" />
            <div className="h-10 w-52 bg-ocean-800/60 rounded-full animate-pulse" />
          </div>
        </div>
      </div>

      {/* Tabs skeleton */}
      <GlassPanel variant="strong" className="p-0 animate-pulse">
        <div className="flex gap-1 px-2 py-2 border-b border-foam-50/10">
          {Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className="h-10 w-28 bg-ocean-800/60 rounded-xl shrink-0"
            />
          ))}
        </div>
        <div className="p-6 grid gap-3 min-h-[300px]">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className={cn(
                'h-3 bg-ocean-800/60 rounded',
                i % 3 === 0 ? 'w-5/6' : i % 3 === 1 ? 'w-4/6' : 'w-3/6',
              )}
            />
          ))}
        </div>
      </GlassPanel>
    </div>
  );
}
